import { Controller } from "@hotwired/stimulus"
import { errorDetail, formatDate, formatSubcatLabel, getAllJSON, sendJSON } from "../api.js"

// Powiadomienia popytu (backorder): lista + włącz + wyłącz + usuń.
// „Wyłącz" = PATCH {is_active: false} (miękkie, odwracalne wyłączenie); „Włącz" = POST
// tych samych parametrów (API reaktywuje istniejące powiadomienie — idempotentne).
// „Usuń" = DELETE (twarde miękkie usunięcie — wiersz znika z listy).
// `organization` jest wysyłana JAWNIE: użytkownik w wielu org (i personel) wybiera
// ją z selecta — bez tego API odpowiada 400 (auto tylko przy dokładnie jednej org).
// Kształt BUR: subcategory + opcjonalnie voivodeship.
// Kształt Insurance: category + voivodeship (wymagane) + opcjonalnie county.
export default class extends Controller {
  static targets = [
    "rows", "status", "subcatSelect", "voivSelect", "orgWrap", "orgSelect",
    "formError", "emailFreqGroup", "smsDailyCheck",
    "verticalTab", "burForm", "insuranceForm", "categorySelect", "insVoivSelect", "countySelect",
  ]
  static values = {
    url: String,               // /api/backorder-subscriptions/
    subcategoriesUrl: String,  // /api/subcategories/
    voivodeshipsUrl: String,   // /api/voivodeships/
    organizationsUrl: String,  // /api/organizations/
    categoriesUrl: String,     // /api/categories/
  }

  async connect() {
    this.subcatNames = new Map()
    this.voivNames = new Map()
    await this.#loadDictionaries()
    await this.load()
  }

  async load() {
    let subscriptions
    try {
      subscriptions = await getAllJSON(this.urlValue)
    } catch {
      this.#status("Nie udało się załadować powiadomień. Odśwież stronę.")
      return
    }
    this.rowsTarget.textContent = ""
    if (!subscriptions.length) {
      this.#status("Brak powiadomień. Dodaj pierwsze powyżej.")
      return
    }
    this.#status(null)
    const template = document.getElementById("backorder-row-template")
    for (const sub of subscriptions) {
      const row = template.content.firstElementChild.cloneNode(true)
      row.dataset.backorderRow = sub.id

      const isInsurance = sub.category != null

      // Kolumna PODKATEGORIA / KATEGORIA
      row.querySelector('[data-field="subcat"]').textContent = isInsurance
        ? (sub.category_name ?? `#${sub.category}`)
        : (sub.subcategory_name ?? this.subcatNames.get(sub.subcategory) ?? `#${sub.subcategory}`)

      // Kolumna WOJEWÓDZTWO (+ powiat dla insurance)
      if (isInsurance) {
        const voivText = sub.voivodeship_name ?? `#${sub.voivodeship}`
        const countyText = sub.county_name ? `, pow. ${sub.county_name}` : ", dowolny powiat"
        row.querySelector('[data-field="voiv"]').textContent = voivText + countyText
      } else {
        row.querySelector('[data-field="voiv"]').textContent =
          sub.voivodeship
            ? (sub.voivodeship_name ?? this.voivNames.get(sub.voivodeship) ?? `#${sub.voivodeship}`)
            : "Dowolne"
      }

      const badge = row.querySelector('[data-field="badge"]')
      badge.className = `ls-badge ${sub.is_active ? "ls-badge--sent" : "ls-badge--new"}`
      badge.textContent = sub.is_active ? "aktywna" : "wyłączona"

      row.querySelector('[data-field="count"]').textContent = sub.notify_count
      row.querySelector('[data-field="last"]').textContent =
        sub.last_notified_at ? formatDate(sub.last_notified_at) : "—"

      const freqLabel = { daily: "raz dziennie", weekly: "raz w tygodniu", off: "wyłączony" }[sub.email_frequency] ?? sub.email_frequency
      const smsText = sub.sms_daily ? "tak" : "nie"
      row.querySelector('[data-field="channels"]').textContent = `E-mail: ${freqLabel} · SMS: ${smsText}`

      const toggle = row.querySelector('[data-field="toggle"]')
      toggle.textContent = sub.is_active ? "Wyłącz" : "Włącz"
      toggle.setAttribute("data-backorder-id-param", sub.id)
      toggle.setAttribute("data-backorder-active-param", sub.is_active)
      toggle.setAttribute("data-backorder-email-frequency-param", sub.email_frequency ?? "daily")
      toggle.setAttribute("data-backorder-sms-daily-param", sub.sms_daily ? "true" : "false")
      toggle.setAttribute("data-backorder-organization-param", sub.organization)
      toggle.setAttribute("data-backorder-voivodeship-param", sub.voivodeship ?? "")

      if (isInsurance) {
        toggle.setAttribute("data-backorder-category-param", sub.category)
        toggle.setAttribute("data-backorder-county-param", sub.county ?? "")
      } else {
        toggle.setAttribute("data-backorder-subcategory-param", sub.subcategory)
      }

      const deleteBtn = row.querySelector('[data-field="delete"]')
      if (deleteBtn) {
        deleteBtn.setAttribute("data-backorder-id-param", sub.id)
      }

      this.rowsTarget.appendChild(row)
    }
  }

  switchVertical(event) {
    const v = event.currentTarget.dataset.vertical
    if (this.hasInsuranceFormTarget) this.insuranceFormTarget.hidden = v !== "ubezpieczenia"
    if (this.hasBurFormTarget) this.burFormTarget.hidden = v !== "bur"
    for (const tab of this.verticalTabTargets) {
      tab.classList.toggle("is-active", tab.dataset.vertical === v)
    }
    this.#formError(null)
  }

  async insVoivChange() {
    const id = this.insVoivSelectTarget.value
    // Reset powiatu do opcji domyślnej
    this.countySelectTarget.textContent = ""
    const emptyOpt = document.createElement("option")
    emptyOpt.value = ""
    emptyOpt.textContent = "Dowolny powiat"
    this.countySelectTarget.appendChild(emptyOpt)

    if (!id) {
      this.countySelectTarget.disabled = true
      return
    }

    let counties
    try {
      counties = await getAllJSON(`/api/counties/?voivodeship=${id}`)
    } catch {
      // błąd sieci — zostaw powiat pusty
      return
    }
    for (const c of counties) {
      const opt = document.createElement("option")
      opt.value = c.id
      opt.textContent = c.name
      this.countySelectTarget.appendChild(opt)
    }
    this.countySelectTarget.disabled = false
  }

  async subscribeInsurance() {
    const category = this.categorySelectTarget.value
    if (!category) {
      this.#formError("Wybierz kategorię.")
      return
    }
    const voivodeship = this.insVoivSelectTarget.value
    if (!voivodeship) {
      this.#formError("Wybierz województwo.")
      return
    }
    const body = {
      category: Number(category),
      voivodeship: Number(voivodeship),
      email_frequency: this.#emailFrequency(),
      sms_daily: this.#smsDaily(),
    }
    if (this.hasCountySelectTarget && this.countySelectTarget.value) {
      body.county = Number(this.countySelectTarget.value)
    }
    const organization = this.#chosenOrganization()
    if (organization) body.organization = organization
    const res = await sendJSON("POST", this.urlValue, body)
    if (!res.ok) {
      this.#formError(errorDetail(res.data, "Nie udało się dodać powiadomienia."))
      return
    }
    this.#formError(null)
    await this.load()
  }

  async subscribe() {
    const subcategory = this.subcatSelectTarget.value
    if (!subcategory) {
      this.#formError("Wybierz podkategorię.")
      return
    }
    const body = {
      subcategory: Number(subcategory),
      email_frequency: this.#emailFrequency(),
      sms_daily: this.#smsDaily(),
    }
    const voivodeship = this.voivSelectTarget.value
    if (voivodeship) body.voivodeship = Number(voivodeship)
    const organization = this.#chosenOrganization()
    if (organization) body.organization = organization
    const res = await sendJSON("POST", this.urlValue, body)
    if (!res.ok) {
      this.#formError(errorDetail(res.data, "Nie udało się dodać powiadomienia."))
      return
    }
    this.#formError(null)
    await this.load()
  }

  async toggleActive(event) {
    const { id, active, subcategory, voivodeship, organization, emailFrequency, smsDaily } = event.params
    let res
    if (active) {
      // Dezaktywacja = PATCH {is_active: false} (miękkie wyłączenie, odwracalne)
      res = await sendJSON("PATCH", `${this.urlValue}${id}/`, { is_active: false })
    } else if (event.params.category) {
      // Reaktywacja kształtu insurance — POST {category, voivodeship, county?, organization, ...}
      const body = {
        category: Number(event.params.category),
        voivodeship: Number(voivodeship),
        organization: Number(organization),
        email_frequency: emailFrequency ?? "daily",
        sms_daily: smsDaily === true || smsDaily === "true",
      }
      if (event.params.county) body.county = Number(event.params.county)
      res = await sendJSON("POST", this.urlValue, body)
    } else {
      // Reaktywacja kształtu BUR — POST {subcategory, voivodeship?, organization, ...}
      const body = {
        subcategory: Number(subcategory),
        organization: Number(organization),
        email_frequency: emailFrequency ?? "daily",
        sms_daily: smsDaily === true || smsDaily === "true",
      }
      if (voivodeship) body.voivodeship = Number(voivodeship)
      res = await sendJSON("POST", this.urlValue, body)
    }
    if (!res.ok) {
      this.#formError(errorDetail(res.data, "Nie udało się zmienić powiadomienia."))
      return
    }
    await this.load()
  }

  deleteSubscription(event) {
    const { id } = event.params
    const btn = event.currentTarget
    if (btn.dataset.confirmPending === "true") {
      this.#doDelete(Number(id))
    } else {
      btn.dataset.confirmPending = "true"
      btn.textContent = "Na pewno usunąć?"
    }
  }

  // --- prywatne ---

  async #doDelete(id) {
    const row = this.rowsTarget.querySelector(`[data-backorder-row="${id}"]`)
    const res = await sendJSON("DELETE", `${this.urlValue}${id}/`)
    if (!res.ok) {
      this.#formError(errorDetail(res.data, "Nie udało się usunąć powiadomienia."))
      return
    }
    row?.remove()
    if (!this.rowsTarget.firstElementChild) {
      this.#status("Brak powiadomień. Dodaj pierwsze powyżej.")
    }
  }

  #emailFrequency() {
    if (!this.hasEmailFreqGroupTarget) return "daily"
    const checked = [...this.emailFreqGroupTarget.querySelectorAll("input[type=radio]")].find(r => r.checked)
    return checked?.value ?? "daily"
  }

  #smsDaily() {
    return this.hasSmsDailyCheckTarget ? this.smsDailyCheckTarget.checked : false
  }

  async #loadDictionaries() {
    try {
      const fetches = [
        getAllJSON(this.subcategoriesUrlValue),
        getAllJSON(this.voivodeshipsUrlValue),
        this.organizationsUrlValue ? getAllJSON(this.organizationsUrlValue) : Promise.resolve([]),
      ]
      if (this.categoriesUrlValue) {
        fetches.push(getAllJSON(this.categoriesUrlValue + "?vertical=ubezpieczenia"))
      }
      const [subcategories, voivodeships, organizations, categories] = await Promise.all(fetches)
      for (const sub of subcategories) {
        // Label „Kategoria › Podkategoria" dla wierszy listy — ten sam helper co w autocomplete
        this.subcatNames.set(sub.id, formatSubcatLabel(sub))
      }
      for (const voivodeship of voivodeships) {
        this.voivNames.set(voivodeship.id, voivodeship.name)
        if (this.hasVoivSelectTarget) {
          const option = document.createElement("option")
          option.value = voivodeship.id
          option.textContent = voivodeship.name
          this.voivSelectTarget.appendChild(option)
        }
        // Repopulate insVoivSelect with the same list
        if (this.hasInsVoivSelectTarget) {
          const option = document.createElement("option")
          option.value = voivodeship.id
          option.textContent = voivodeship.name
          this.insVoivSelectTarget.appendChild(option)
        }
      }
      this.organizations = organizations
      if (this.hasOrgSelectTarget && organizations.length > 1) {
        for (const org of organizations) {
          const option = document.createElement("option")
          option.value = org.id
          option.textContent = org.name
          this.orgSelectTarget.appendChild(option)
        }
        if (this.hasOrgWrapTarget) this.orgWrapTarget.hidden = false
      }
      if (categories && this.hasCategorySelectTarget) {
        for (const cat of categories) {
          const option = document.createElement("option")
          option.value = cat.id
          option.textContent = cat.name
          this.categorySelectTarget.appendChild(option)
        }
      }
    } catch {
      // słowniki niedostępne — formularz zostaje pusty, lista pokaże #id
    }
  }

  #chosenOrganization() {
    if (this.hasOrgSelectTarget && !this.orgSelectTarget.closest("[hidden]") && this.orgSelectTarget.value) {
      return Number(this.orgSelectTarget.value)
    }
    // Jedna org: wyślij ją jawnie (spójne z auto po stronie API; personel z 1 org też zadziała).
    if (this.organizations?.length === 1) return Number(this.organizations[0].id)
    return null
  }

  #formError(message) {
    if (!this.hasFormErrorTarget) return
    this.formErrorTarget.hidden = message === null
    if (message !== null) this.formErrorTarget.textContent = message
  }

  #status(message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.hidden = message === null
    if (message !== null) this.statusTarget.textContent = message
  }
}
